import axios from 'axios';
import { message } from 'antd';


// export const API_BASE_URL = "http://localhost:8080/api";
 export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const FAILURE_STATUS_VALUES = new Set(['error', 'failed', 'failure', 'fail']);
const ERROR_TOAST_DEDUPE_MS = 2000;
let lastErrorToast = { text: '', timestamp: 0 };

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const getPayloadErrorMessage = (payload) => {
  if (!payload) {
    return null;
  }

  if (typeof Blob !== 'undefined' && payload instanceof Blob) {
    return null;
  }


  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    return trimmed || null;
  }

  if (!isPlainObject(payload)) {
    return null;
  }

  const directKeys = ['error', 'message', 'details', 'detail', 'reason', 'title'];
  for (const key of directKeys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  if (isPlainObject(payload.error) && typeof payload.error.message === 'string' && payload.error.message.trim()) {
    return payload.error.message.trim();
  }

  if (Array.isArray(payload.errors) && payload.errors.length > 0) {
    const firstError = payload.errors[0];

    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError.trim();
    }

    if (isPlainObject(firstError)) {
      const nestedMessage = firstError.message || firstError.msg || firstError.error;
      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        return nestedMessage.trim();
      }
    }
  }

  return null;
};

const getLogicalFailureMessage = (response) => {
  const payload = response?.data;

  if (!isPlainObject(payload)) {
    return null;
  }

  const hasSuccessFlag = Object.prototype.hasOwnProperty.call(payload, 'success');
  const hasOkFlag = Object.prototype.hasOwnProperty.call(payload, 'ok');
  const statusValue = typeof payload.status === 'string'
    ? payload.status.trim().toLowerCase()
    : null;

  if (hasSuccessFlag && payload.success === false) {
    return getPayloadErrorMessage(payload) || 'Request failed.';
  }

  if (hasOkFlag && payload.ok === false) {
    return getPayloadErrorMessage(payload) || 'Request failed.';
  }

  if (statusValue && FAILURE_STATUS_VALUES.has(statusValue)) {
    return getPayloadErrorMessage(payload) || 'Request failed.';
  }

  return null;
};

const notifyApiError = (errorText) => {
  if (typeof window === 'undefined' || !errorText) {
    return;
  }

  const now = Date.now();
  if (
    lastErrorToast.text === errorText
    && now - lastErrorToast.timestamp < ERROR_TOAST_DEDUPE_MS
  ) {
    return;
  }

  lastErrorToast = { text: errorText, timestamp: now };
  message.error(errorText);
};

export const getApiErrorMessage = (
  error,
  fallbackMessage = 'Something went wrong. Please try again.'
) => {
  if (!error) {
    return fallbackMessage;
  }

  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }

  if (typeof error.userMessage === 'string' && error.userMessage.trim()) {
    return error.userMessage.trim();
  }

  const payloadMessage = getPayloadErrorMessage(error.response?.data);
  if (payloadMessage) {
    return payloadMessage;
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }

  return fallbackMessage;
};

const createNormalizedApiError = (
  errorMessage,
  originalError = {},
  responseOverride
) => {
  const normalizedError = originalError instanceof Error
    ? originalError
    : new Error(errorMessage);

  normalizedError.userMessage = errorMessage;
  normalizedError.response = responseOverride || originalError?.response;

  const responsePayload = normalizedError.response?.data;
  if (normalizedError.response) {
    if (isPlainObject(responsePayload)) {
      if (!responsePayload.error) {
        normalizedError.response.data = {
          ...responsePayload,
          error: errorMessage,
        };
      }
    } else if (responsePayload == null) {
      normalizedError.response.data = { error: errorMessage };
    } else {
      normalizedError.response.data = {
        error: errorMessage,
        raw: responsePayload,
      };
    }
  }

  return normalizedError;
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    const logicalFailureMessage = getLogicalFailureMessage(response);

    if (!logicalFailureMessage) {
      return response;
    }

    const normalizedError = createNormalizedApiError(
      logicalFailureMessage,
      new Error(logicalFailureMessage),
      response
    );

    if (!response?.config?.skipGlobalErrorHandler) {
      notifyApiError(logicalFailureMessage);
    }

    return Promise.reject(normalizedError);
  },
  (error) => {
    if (error?.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const errorMessage = getApiErrorMessage(error, 'Request failed. Please try again.');
    const normalizedError = createNormalizedApiError(errorMessage, error);

    // Handle 401 Unauthorized - redirect to login
    if (normalizedError.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('permissions');

      if (!error?.config?.skipGlobalErrorHandler) {
        notifyApiError('Your session has expired. Please login again.');
      }

      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }

      return Promise.reject(normalizedError);
    }

    if (!error?.config?.skipGlobalErrorHandler) {
      notifyApiError(errorMessage);
    }

    return Promise.reject(normalizedError);
  }
);

// Auth API endpoints
export const authAPI = {
  // Basic authentication
  login: (credentials) => {
    console.log('API: Making login request with:', credentials);
    return api.post('/auth/login', credentials);
  },
  register: (userData) => {
    console.log('API: Making register request with:', userData);
    console.log('API: Base URL:', API_BASE_URL);
    console.log('API: Full URL will be:', `${API_BASE_URL}/auth/register`);
    return api.post('/auth/register', userData);
  },
  logout: () => api.post('/auth/logout'),
  
  // Password management
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword, newPassword) => api.post('/user/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
  
  // Email verification
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  
  // User profile management
  getProfile: () => api.get('/user/profile'),
  updateProfile: (userData) => api.put('/user/profile', userData),
  
  // Admin functions
  createAdmin: (userData) => api.post('/auth/create-admin', userData),
  
  // Legacy signup for backward compatibility
  signup: (userData) => api.post('/auth/register', userData),
};

// Purchase Orders API endpoints
export const ordersAPI = {
  fetchOrders: (page = 1, limit = 10) => api.get('/purchase-orders', { params: { page, limit } }),
  getOrder: (id) => api.get(`/purchase-orders/${id}`),
  createOrder: (orderData) => {
    return api.post('/purchase-orders', orderData, {
      responseType: 'blob', // Important for handling PDF downloads
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/pdf', // Explicitly request PDF
      },
    });
  },
  updateOrder: (id, orderData) => api.put(`/purchase-orders/${id}`, orderData),
  acceptShortfall: (poId, itemId, data) => api.patch(`/purchase-orders/${poId}/items/${itemId}/accept-shortfall`, data),
  deleteOrder: (id) => api.delete(`/purchase-orders/${id}`),
  redownloadOrder: (id) => {
    return api.get(`/purchase-orders/${id}/pdf`, {
      responseType: 'blob', // Important for handling PDF downloads
      headers: {
        'Accept': 'application/pdf', // Explicitly request PDF
      },
    });
  },
};

// Vendors API endpoints
export const vendorsAPI = {
  fetchVendors: () => api.get('/vendors'),
  getVendor: (id) => api.get(`/vendors/${id}`),
  createVendor: (vendorData) => api.post('/vendors', vendorData),
  updateVendor: (id, vendorData) => api.put(`/vendors/${id}`, vendorData),
};

// KP Products API endpoints
export const productsAPI = {
  fetchProducts: () => api.get('/kp-products'),
  getProduct: (id) => api.get(`/kp-products/${id}`),
  createProduct: (productData) => api.post('/kp-products', productData),
  updateProduct: (id, productData) => api.put(`/kp-products/${id}`, productData),
};

// Audit Logs API endpoints
export const auditLogsAPI = {
  // Get audit logs with comprehensive filtering and pagination
  fetchAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add all supported query parameters
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.action) queryParams.append('action', params.action);
    if (params.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const queryString = queryParams.toString();
    return api.get(`/audits${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get specific audit log by ID
  getAuditLog: (id) => api.get(`/audits/${id}`),
  
  // Get audit summary with date filtering
  getAuditSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return api.get(`/audits/summary${queryString ? `?${queryString}` : ''}`);
  },
};

// Udhyam Categories API endpoints
export const udhyamCategoriesAPI = {
  // Get all categories with optional filtering
  fetchUdhyamCategories: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.category_name) queryParams.append('category_name', params.category_name);
    
    const queryString = queryParams.toString();
    return api.get(`/udhyam-categories${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get only active categories
  fetchActiveUdhyamCategories: () => api.get('/udhyam-categories/active'),
  
  // Get category by ID
  getUdhyamCategory: (id) => api.get(`/udhyam-categories/${id}`),
  
  // Create new category
  createUdhyamCategory: (categoryData) => api.post('/udhyam-categories', categoryData),
  
  // Update existing category
  updateUdhyamCategory: (id, categoryData) => api.put(`/udhyam-categories/${id}`, categoryData),
  
  // Delete category
  deleteUdhyamCategory: (id) => api.delete(`/udhyam-categories/${id}`),
};

// Product Categories API endpoints
export const productCategoriesAPI = {
  // Get all categories with optional filtering
  fetchProductCategories: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.category_name) queryParams.append('category_name', params.category_name);
    
    const queryString = queryParams.toString();
    return api.get(`/product-categories${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get only active categories
  fetchActiveProductCategories: () => api.get('/product-categories/active'),
  
  // Get category by ID
  getProductCategory: (id) => api.get(`/product-categories/${id}`),
  
  // Create new category
  createProductCategory: (categoryData) => api.post('/product-categories', categoryData),
  
  // Update existing category
  updateProductCategory: (id, categoryData) => api.put(`/product-categories/${id}`, categoryData),
  };

// Home API endpoints
export const homeAPI = {
  fetchHomeData: () => api.get('/kp/home'),
};

// Delivery Address API endpoints
export const deliveryAddressAPI = {
  fetchDeliveryAddresses: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.is_default !== undefined) queryParams.append('is_default', params.is_default);
    if (params.city) queryParams.append('city', params.city);
    if (params.state) queryParams.append('state', params.state);
    if (params.pincode) queryParams.append('pincode', params.pincode);
    
    const queryString = queryParams.toString();
    return api.get(`/delivery-addresses${queryString ? `?${queryString}` : ''}`);
  },
  getDeliveryAddress: (id) => api.get(`/delivery-addresses/${id}`),
  createDeliveryAddress: (addressData) => api.post('/delivery-addresses', addressData),
  updateDeliveryAddress: (id, addressData) => api.put(`/delivery-addresses/${id}`, addressData),
};

// Firm Names API endpoints
export const firmNamesAPI = {
  // Get all firm names with optional filtering
  fetchFirmNames: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.firm_name) queryParams.append('firm_name', params.firm_name);
    
    const queryString = queryParams.toString();
    return api.get(`/firm-names${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get only active firm names
  fetchActiveFirmNames: () => api.get('/firm-names/active'),
  
  // Get firm name by ID
  getFirmName: (id) => api.get(`/firm-names/${id}`),
  
  // Create new firm name
  createFirmName: (firmData) => api.post('/firm-names', firmData),
  
  // Update existing firm name
  updateFirmName: (id, firmData) => api.put(`/firm-names/${id}`, firmData),
};

// RBAC API endpoints
export const rbacAPI = {
  // Role Management
  fetchRoles: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.enable !== undefined) queryParams.append('enable', params.enable);
    if (params.status !== undefined) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    return api.get(`/rbac/roles${queryString ? `?${queryString}` : ''}`);
  },
  getRole: (id) => api.get(`/rbac/roles/${id}`),
  createRole: (roleData) => api.post('/rbac/roles', roleData),
  updateRole: (id, roleData) => api.put(`/rbac/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  getRoleUsers: (roleId) => api.get(`/rbac/roles/${roleId}/users`),
  
  // Action Management
  fetchActions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.type) queryParams.append('type', params.type);
    
    const queryString = queryParams.toString();
    return api.get(`/rbac/actions${queryString ? `?${queryString}` : ''}`);
  },
  getActionsGrouped: () => api.get('/rbac/actions/grouped'),
  getAction: (id) => api.get(`/rbac/actions/${id}`),
  createAction: (actionData) => api.post('/rbac/actions', actionData),
  updateAction: (id, actionData) => api.put(`/rbac/actions/${id}`, actionData),
  deleteAction: (id) => api.delete(`/rbac/actions/${id}`),
  
  // Permission Management
  getRolePermissions: (roleId) => api.get(`/rbac/roles/${roleId}/permissions`),
  assignPermissions: (data) => api.post('/rbac/roles/assign-permissions', data),
  bulkAssignPermissions: (data) => api.post('/rbac/roles/bulk-assign-permissions', data),
  togglePermission: (data) => api.post('/rbac/roles/toggle-permission', data),
  
  // User Management
  fetchUsers: () => api.get('/rbac/users'),
  getUser: (id) => api.get(`/rbac/users/${id}`),
  updateUserRole: (userId, roleId) => api.put(`/rbac/users/${userId}/role`, { role_id: roleId }),
  removeUserRole: (userId) => api.delete(`/rbac/users/${userId}/role`),
  assignRole: (data) => api.post('/rbac/users/assign-role', data),
  getUserPermissions: (userId) => api.get(`/rbac/users/${userId}/permissions`),
  checkPermission: (data) => api.post('/rbac/users/check-permission', data),
  
  // My Permissions
  getMyPermissions: () => api.get('/my-permissions'),
};

// Delivery Challan API endpoints
export const deliveryAPI = {
  // Simple delivery recording (POST /api/delivery/record)
  recordDelivery: (data) => api.post('/delivery/record', data),

  // Update delivery status (POST /api/delivery/update-status)
  updateDeliveryStatus: (data) => api.post('/delivery/update-status', data),

  // Get challan by number (GET /api/delivery/challan?challan_number=)
  getChallanByNumber: (challanNumber) =>
    api.get('/delivery/challan', { params: { challan_number: challanNumber } }),

  // List all challans (GET /api/delivery/challans)
  listChallans: (params = {}) => {
    const p = {};
    if (params.page != null) p.page = params.page;
    if (params.limit != null) p.limit = params.limit;
    if (params.purchase_order_id != null) p.purchase_order_id = params.purchase_order_id;
    if (params.delivery_status) p.delivery_status = params.delivery_status;
    return api.get('/delivery/challans', { params: p });
  },

  // Get delivery statistics (GET /api/delivery/statistics)
  getStatistics: () => api.get('/delivery/statistics'),

  // Delete challan (DELETE /api/delivery/challan/:id)
  deleteChallan: (id) => api.delete(`/delivery/challan/${id}`),

  // Create challan with items — detailed (POST /api/delivery-challans)
  createChallanWithItems: (data) => api.post('/delivery-challans', data),

  // Get challan by ID (GET /api/delivery-challans/:id)
  getChallanById: (id) => api.get(`/delivery-challans/${id}`),

  // List challans — alternate detailed route (GET /api/delivery-challans)
  listChallansDetailed: (params = {}) => {
    const p = {};
    if (params.page) p.page = params.page;
    if (params.limit) p.limit = params.limit;
    if (params.purchase_order_id) p.purchase_order_id = params.purchase_order_id;
    if (params.delivery_status) p.delivery_status = params.delivery_status;
    return api.get('/delivery-challans', { params: p });
  },

  // Get PO delivery status (GET /api/purchase-orders/:id/delivery-status)
  getPODeliveryStatus: (poId) => api.get(`/purchase-orders/${poId}/delivery-status`),
};

// Bill Receipt API endpoints
export const billAPI = {
  // POST /api/bills/receive  – record a new vendor bill against one or more challans
  receiveBill: (data) => api.post('/bills/receive', data),

  // GET /api/bills/:id  – fetch a single bill receipt (with ChallanLinks + Items)
  getBillById: (id) => api.get(`/bills/${id}`),

  // PATCH /api/bills/:id/verify  – move received → verified (triggers reconciliation)
  verifyBill: (id, data = {}) => api.patch(`/bills/${id}/verify`, data),

  // PATCH /api/bills/:id/reject  – reject received|verified bill; reverts challans to pending
  rejectBill: (id, data = {}) => api.patch(`/bills/${id}/reject`, data),

  // PATCH /api/bills/:id/close  – move verified → closed (final, irreversible)
  closeBill: (id, data = {}) => api.patch(`/bills/${id}/close`, data),

  // GET /api/bills  – paginated list with optional filters
  listBills: (params = {}) => {
    const p = {};
    if (params.page != null) p.page = params.page;
    if (params.limit != null) p.limit = params.limit;
    if (params.purchase_order_id) p.purchase_order_id = params.purchase_order_id;
    if (params.delivery_challan_id) p.delivery_challan_id = params.delivery_challan_id;
    if (params.bill_status) p.bill_status = params.bill_status;
    if (params.reconciliation_status) p.reconciliation_status = params.reconciliation_status;
    return api.get('/bills', { params: p });
  },

  // GET /api/bills/pending-challans  – challans with no active bill yet
  getPendingChallans: (params = {}) => {
    const p = {};
    if (params.page != null) p.page = params.page;
    if (params.limit != null) p.limit = params.limit;
    if (params.purchase_order_id) p.purchase_order_id = params.purchase_order_id;
    return api.get('/bills/pending-challans', { params: p });
  },

  // GET /api/purchase-orders/:id/bill-summary  – per-PO summary card
  getPOBillSummary: (poId) => api.get(`/purchase-orders/${poId}/bill-summary`),

  // GET /api/purchase-orders/:id/reconciliation  – full 3-way recon report
  getPOReconciliation: (poId) => api.get(`/purchase-orders/${poId}/reconciliation`),

  // GET /api/bills/statistics  – dashboard aggregates
  getStatistics: () => api.get('/bills/statistics'),
};

// Intelligence API endpoints (anomalies, savings, month comparison)
export const intelligenceAPI = {
  getBillAnomalies: (billId) => api.get(`/bills/${billId}/anomalies`),
  getPOSavings:     (poId)   => api.get(`/purchase-orders/${poId}/savings`),
  getMonthComparison: (params = {}) => {
    const p = {};
    if (params.months)    p.months    = params.months;
    if (params.vendor_id) p.vendor_id = params.vendor_id;
    return api.get('/analytics/month-comparison', { params: p });
  },
};

// Vendor Statement API
export const vendorStatementAPI = {
  getStatement: (vendorId, params = {}) => {
    const p = { format: params.format || 'json' };
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to)   p.date_to   = params.date_to;
    return api.get(`/vendors/${vendorId}/statement`, {
      params: p,
      ...(params.format === 'pdf' ? { responseType: 'blob' } : {}),
    });
  },
};

// Recurring Purchase Orders API (admin only)
export const recurringPOAPI = {
  create:     (data)       => api.post('/recurring-pos', data),
  list:       (activeOnly) => api.get('/recurring-pos', { params: { active_only: activeOnly } }),
  get:        (id)         => api.get(`/recurring-pos/${id}`),
  update:     (id, data)   => api.patch(`/recurring-pos/${id}`, data),
};

// Returns API endpoints
export const returnsAPI = {
  create: (data) => api.post('/returns', data),
  list: (params = {}) => {
    const p = {};
    if (params.vendor_id)         p.vendor_id         = params.vendor_id;
    if (params.purchase_order_id) p.purchase_order_id = params.purchase_order_id;
    if (params.status)            p.status            = params.status;
    return api.get('/returns', { params: p });
  },
  get: (id) => api.get(`/returns/${id}`),
  approve: (id) => api.post(`/returns/${id}/approve`),
  updateStatus: (id, data) => api.patch(`/returns/${id}/status`, data),
};

// Credit Notes API endpoints
export const creditNotesAPI = {
  create: (data) => api.post('/credit-notes', data),
  list: (params = {}) => {
    const p = {};
    if (params.vendor_id) p.vendor_id = params.vendor_id;
    if (params.status)    p.status    = params.status;
    return api.get('/credit-notes', { params: p });
  },
  get: (id) => api.get(`/credit-notes/${id}`),
  adjust: (id, data) => api.patch(`/credit-notes/${id}/adjust`, data),
};

// Quotations API endpoints
export const quotationsAPI = {
  create: (data) => api.post('/quotations', data),
  list: (params = {}) => {
    const p = {};
    if (params.vendor_id)  p.vendor_id  = params.vendor_id;
    if (params.status)     p.status     = params.status;
    if (params.date_from)  p.date_from  = params.date_from;
    if (params.date_to)    p.date_to    = params.date_to;
    return api.get('/quotations', { params: p });
  },
  get: (id) => api.get(`/quotations/${id}`),
  updateStatus: (id, data) => api.patch(`/quotations/${id}/status`, data),
  convert: (id, data) => api.post(`/quotations/${id}/convert`, data),
  getComparative: (params = {}) => {
    const p = {};
    if (params.product_ids) p.product_ids = params.product_ids;
    if (params.vendor_ids)  p.vendor_ids  = params.vendor_ids;
    if (params.date_from)   p.date_from   = params.date_from;
    if (params.date_to)     p.date_to     = params.date_to;
    return api.get('/quotations/comparative', { params: p });
  },
};

// Price History API endpoints
export const priceHistoryAPI = {
  getProductHistory: (productId, params = {}) => {
    const p = {};
    if (params.vendor_id) p.vendor_id = params.vendor_id;
    return api.get(`/kp-products/${productId}/price-history`, { params: p });
  },
  getVendorHistory: (vendorId, params = {}) => {
    const p = {};
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to)   p.date_to   = params.date_to;
    return api.get(`/vendors/${vendorId}/price-history`, { params: p });
  },
  getInflationReport: (params = {}) => {
    const p = {};
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to)   p.date_to   = params.date_to;
    return api.get('/kp-products/price-inflation', { params: p });
  },
};

// Approvals API endpoints
export const approvalsAPI = {
  getConfig:          ()         => api.get('/approvals/config'),
  updateConfig:       (data)     => api.put('/approvals/config', data),
  getPendingApprovals: ()        => api.get('/approvals/pending'),
  getPOApprovalStatus: (poId)   => api.get(`/purchase-orders/${poId}/approval`),
  approvePO:          (poId, data) => api.post(`/purchase-orders/${poId}/approve`, data),
  rejectPOApproval:   (poId, data) => api.post(`/purchase-orders/${poId}/reject-approval`, data),
};

// Vendor Performance API endpoints
export const vendorPerformanceAPI = {
  getVendorScore: (vendorId, params = {}) => {
    const p = {};
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to)   p.date_to   = params.date_to;
    return api.get(`/vendors/${vendorId}/performance`, { params: p });
  },
  getAllVendorScores: (params = {}) => {
    const p = {};
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to)   p.date_to   = params.date_to;
    return api.get('/vendors/performance', { params: p });
  },
};

// Payments API endpoints
export const paymentsAPI = {
  createPayment: (data) => api.post('/payments', data),
  listPayments: (params = {}) => {
    const p = {};
    if (params.vendor_id) p.vendor_id = params.vendor_id;
    if (params.purchase_order_id) p.purchase_order_id = params.purchase_order_id;
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to) p.date_to = params.date_to;
    if (params.mode) p.mode = params.mode;
    return api.get('/payments', { params: p });
  },
  getBillPayments: (billId) => api.get(`/bills/${billId}/payments`),
  getBillPaymentSummary: (billId) => api.get(`/bills/${billId}/payment-summary`),
  getPOPayments: (poId) => api.get(`/purchase-orders/${poId}/payments`),
  getStatistics: (params = {}) => {
    const p = {};
    if (params.date_from) p.date_from = params.date_from;
    if (params.date_to) p.date_to = params.date_to;
    return api.get('/payments/statistics', { params: p });
  },
};

// Analytics API endpoints
export const analyticsAPI = {
  getDashboard: (params = {}) => api.get('/purchase-orders/analytics/dashboard', { params }),
  getSummary: (params = {}) => api.get('/purchase-orders/analytics/summary', { params }),
  getVendors: (params = {}) => api.get('/purchase-orders/analytics/vendors', { params }),
  getMonthly: (params = {}) => api.get('/purchase-orders/analytics/monthly', { params }),
  getProducts: (params = {}) => api.get('/purchase-orders/analytics/products', { params }),
  getExpenditure: (params = {}) => api.get('/purchase-orders/analytics/expenditure', { params }),
  getVendorProducts: (params = {}) => api.get('/purchase-orders/analytics/vendor-products', { params }),
  getFirms: (params = {}) => api.get('/purchase-orders/analytics/firms', { params }),
};

export default api;
